/**
 * @voltxampere/engine — ACSolver orchestrator (Sprint 20)
 *
 * AC analysis pipeline:
 *
 *  1. DC operating-point pre-pass via `DCSolver`. If the OP fails
 *     to converge the AC analysis surfaces a warning and returns an
 *     unconverged result — small-signal linearisation cannot
 *     proceed without a converged bias point.
 *
 *  2. `planCircuit(circuit)` once to size the MNA system. Topology
 *     does not change between sweep points so we reuse the plan
 *     across every frequency.
 *
 *  3. `linearizeAtOP` snapshots the DC OP into the
 *     `LinearizedCircuit` envelope. Stamps read from this for
 *     small-signal slopes (rd, gm, rπ, gds).
 *
 *  4. `sweepFrequencies` produces the [f₁, f₂, … fₙ] Float64Array.
 *
 *  5. For each frequency: `buildACMatrix` → `complexLuDecompose` →
 *     `complexLuSolve` → extract magnitude / phase per net + branch.
 *
 *  AbortSignal granularity: poll between frequency points (one
 *  cancellation can interrupt the sweep mid-way). Per-frequency
 *  solve is fast enough (≤ 1 ms for small circuits) that finer
 *  granularity is unnecessary.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { complexLuDecompose, complexLuSolve } from "@voltxampere/math";
import { DCSolver } from "../dc/solver.js";
import { planCircuit } from "../dc/circuit-plan.js";
import { areACStampsRegistered, areDCStampsRegistered, registerACStamps, registerDCStamps, } from "../stamps/register.js";
import { buildACMatrix } from "./matrix.js";
import { sweepFrequencies } from "./sweep.js";
import { DEFAULT_AC_TEMPERATURE_K, linearizeAtOP } from "./linearize.js";
function defaultSweepFromCircuit(circuit) {
    const ac = circuit.simulationSettings.ac;
    return {
        fStart: ac.start,
        fStop: ac.stop,
        points: ac.points,
        sweep: ac.sweep === "lin" ? "lin" : "dec",
    };
}
export class ACSolver {
    async solve(circuit, options = {}) {
        if (!areDCStampsRegistered()) {
            registerDCStamps();
        }
        if (!areACStampsRegistered()) {
            registerACStamps();
        }
        options.signal?.throwIfAborted();
        options.onProgress?.(0, "ac:init");
        // 1. DC OP pre-pass.
        const dcStart = performance.now();
        const dcOpts = options.signal !== undefined ? { signal: options.signal } : {};
        const dcResult = await new DCSolver().solve(circuit, dcOpts);
        const dcOPElapsedMs = performance.now() - dcStart;
        options.onProgress?.(20, "ac:dc-op");
        const warnings = [];
        if (!dcResult.converged) {
            warnings.push("AC analysis aborted: DC operating point did not converge. Linearisation requires a stable OP.");
            return {
                converged: false,
                frequencies: new Float64Array(0),
                nodeMagnitudes: new Map(),
                nodePhases: new Map(),
                branchMagnitudes: new Map(),
                branchPhases: new Map(),
                warnings,
                meta: { dcOPElapsedMs, sweepElapsedMs: 0, sweepPoints: 0 },
            };
        }
        // 2. Plan + linearize.
        const plan = planCircuit(circuit);
        const temperature = options.temperature ?? DEFAULT_AC_TEMPERATURE_K;
        const linearized = linearizeAtOP(circuit, dcResult, temperature);
        // 3. Frequency list.
        const sweepConfig = options.sweep ?? defaultSweepFromCircuit(circuit);
        const frequencies = sweepFrequencies(sweepConfig);
        const points = frequencies.length;
        // 4. Per-net + per-branch trace storage.
        const nodeMagnitudes = new Map();
        const nodePhases = new Map();
        for (const netId of plan.orderedNetIds) {
            nodeMagnitudes.set(netId, new Float64Array(points));
            nodePhases.set(netId, new Float64Array(points));
        }
        const branchMagnitudes = new Map();
        const branchPhases = new Map();
        for (const compId of plan.orderedVSourceIds) {
            branchMagnitudes.set(compId, new Float64Array(points));
            branchPhases.set(compId, new Float64Array(points));
        }
        // 5. Frequency loop.
        const sweepStart = performance.now();
        for (let i = 0; i < points; i++) {
            options.signal?.throwIfAborted();
            const f = frequencies[i] ?? 0;
            const omega = 2 * Math.PI * f;
            const built = buildACMatrix(circuit, plan, linearized, omega);
            let lu;
            try {
                lu = complexLuDecompose(built.Y);
            }
            catch (err) {
                warnings.push(`AC sweep point ${String(i)} (f=${String(f)} Hz): matrix singular — ${err instanceof Error ? err.message : String(err)}`);
                // Mark this point as zero magnitude / phase and continue.
                for (const trace of nodeMagnitudes.values())
                    trace[i] = 0;
                for (const trace of nodePhases.values())
                    trace[i] = 0;
                for (const trace of branchMagnitudes.values())
                    trace[i] = 0;
                for (const trace of branchPhases.values())
                    trace[i] = 0;
                continue;
            }
            const x = complexLuSolve(lu, built.rhs);
            for (let j = 0; j < plan.orderedNetIds.length; j++) {
                const netId = plan.orderedNetIds[j] ?? "";
                const re = x.data[2 * j] ?? 0;
                const im = x.data[2 * j + 1] ?? 0;
                const magArr = nodeMagnitudes.get(netId);
                const phArr = nodePhases.get(netId);
                if (magArr !== undefined)
                    magArr[i] = Math.hypot(re, im);
                if (phArr !== undefined)
                    phArr[i] = Math.atan2(im, re);
            }
            for (let j = 0; j < plan.orderedVSourceIds.length; j++) {
                const compId = plan.orderedVSourceIds[j] ?? "";
                // Sprint 24 B1 — vsource rows live AFTER the internal-node block.
                const idx = plan.nodeCount + plan.internalNodeCount + j;
                const re = x.data[2 * idx] ?? 0;
                const im = x.data[2 * idx + 1] ?? 0;
                const magArr = branchMagnitudes.get(compId);
                const phArr = branchPhases.get(compId);
                if (magArr !== undefined)
                    magArr[i] = Math.hypot(re, im);
                if (phArr !== undefined)
                    phArr[i] = Math.atan2(im, re);
            }
        }
        const sweepElapsedMs = performance.now() - sweepStart;
        options.onProgress?.(100, "ac:done");
        return {
            converged: warnings.length === 0,
            frequencies,
            nodeMagnitudes,
            nodePhases,
            branchMagnitudes,
            branchPhases,
            warnings,
            meta: { dcOPElapsedMs, sweepElapsedMs, sweepPoints: points },
        };
    }
}
//# sourceMappingURL=solver.js.map